import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getServerURL } from '../config/api';
import {
  FaBarcode,
  FaQrcode,
  FaImage,
  FaRobot,
  FaDatabase,
  FaChartLine,
  FaWarehouse,
  FaBox,
  FaWifi,
  FaServer
} from 'react-icons/fa';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import './Dashboard.css';

const Dashboard = () => {
  const { getUserRole } = useAuth();
  const userRole = getUserRole();
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { socket } = useWebSocket();
  // Mock stats removed as requested

  const quickActions = [
    {
      title: 'Scan Barcode',
      description: 'Scan or upload barcode images',
      icon: FaBarcode,
      path: '/scanner',
      color: '#E3821E'
    },
    {
      title: 'Generate Barcode',
      description: 'Create new barcodes with custom data',
      icon: FaQrcode,
      path: '/generator',
      color: '#E3821E'
    },
    {
      title: 'Process Image',
      description: 'Enhance and filter images',
      icon: FaImage,
      path: '/image-processing',
      color: '#E3821E'
    },
    {
      title: 'Rack Management',
      description: 'Manage warehouse racks and products',
      icon: FaWarehouse,
      path: '/rack-management',
      color: '#E3821E'
    },
    {
      title: 'Product Management',
      description: 'Track product movements and inventory',
      icon: FaBox,
      path: '/product-management',
      color: '#E3821E'
    },
    {
      title: '2D Map',
      description: 'Real-time LiDAR mapping and robot tracking',
      icon: FaRobot,
      path: '/robot-control',
      color: '#E3821E'
    }
  ];

  // Fetch system status
  const fetchSystemStatus = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      const serverURL = getServerURL();
      const token = localStorage.getItem('robridge_token');

      // 1. Fetch standard system status
      const statusResponse = await fetch(`${serverURL}/api/system/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statusData = await statusResponse.json();

      // 2. Fetch Helper: Manual Scan Counts (Patch for Deployed Server)
      // We fetch the actual lists to get accurate counts since the server's /status endpoint might be outdated
      const historyPromise = fetch(`${serverURL}/api/barcodes/scanned?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).catch(() => ({ success: false }));

      const savedPromise = fetch(`${serverURL}/api/saved-scans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).catch(() => ({ success: false }));

      const [historyData, savedData] = await Promise.all([historyPromise, savedPromise]);

      let manualScans = null;
      if (historyData.success || savedData.success) {
        const historyCount = historyData.success && historyData.barcodes ? historyData.barcodes.length : 0;
        const savedCount = savedData.success && savedData.savedScans ? savedData.savedScans.length : 0;

        // Calculate today's scans from history
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayCount = historyData.success && historyData.barcodes
          ? historyData.barcodes.filter(b => new Date(b.created_at) >= today).length
          : 0;

        manualScans = {
          total: historyCount + savedCount,
          today: todayCount
        };
      }

      if (statusData.success) {
        // Merge manual counts if available, otherwise use server counts
        setSystemStatus({
          ...statusData.status,
          scans: manualScans || statusData.status.scans
        });
      } else {
        console.error('Failed to fetch system status:', statusData.error);
      }
    } catch (error) {
      console.error('Error fetching system status:', error);
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
      setIsRefreshing(false);
    }
  }, [setLoading, setIsRefreshing, setSystemStatus]);

  // Initial load and interval
  useEffect(() => {
    fetchSystemStatus(false);
    const interval = setInterval(() => fetchSystemStatus(true), 30000);
    return () => clearInterval(interval);
  }, [fetchSystemStatus]);

  // WebSocket listeners
  useEffect(() => {
    if (socket) {
      const handleUpdate = () => {
        console.log('Global update received on dashboard');
        fetchSystemStatus(true);
      };

      socket.on('device_paired', handleUpdate);
      socket.on('device_unpaired', handleUpdate);
      socket.on('barcode_scanned', handleUpdate);

      return () => {
        socket.off('device_paired', handleUpdate);
        socket.off('device_unpaired', handleUpdate);
        socket.off('barcode_scanned', handleUpdate);
      };
    }
  }, [socket, fetchSystemStatus]);

  // Helper function to get status class
  const getStatusClass = (status) => {
    switch (status) {
      case 'connected':
      case 'online':
      case 'optimal':
        return 'status-connected';
      case 'warning':
        return 'status-warning';
      case 'error':
        return 'status-error';
      default:
        return 'status-unknown';
    }
  };

  // Helper function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'online':
        return 'Online';
      case 'optimal':
        return 'Optimal';
      case 'warning':
        return 'Warning';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Barcode Management System Overview</p>
      </div>

      {/* Mock stats cards removed as requested */}

      <div className="dashboard-tiles">
        {/* Quick Actions Tiles - Hidden for Expo Users */}
        {userRole !== ROLES.EXPO_USER && (
          <div className="tiles-section">
            <h2>Quick Actions</h2>
            <div className="tiles-grid">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Link key={index} to={action.path} className="dashboard-tile">
                    <div className="tile-header">
                      <div className="tile-icon" style={{ backgroundColor: action.color }}>
                        <Icon size={20} />
                      </div>
                      <div className="tile-title">{action.title}</div>
                    </div>
                    <div className="tile-content">
                      <p>{action.description}</p>
                    </div>
                    <div className="tile-footer">
                      <span className="tile-arrow">→</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}


        {/* System Status Tiles */}
        <div className="tiles-section">
          <h2>System Status</h2>
          {loading ? (
            <div className="status-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
              <div className="spinner-small" style={{
                width: '20px',
                height: '20px',
                border: '2px solid #E3821E',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ margin: 0 }}>Loading system status...</p>
            </div>
          ) : systemStatus ? (
            <div className="tiles-grid">
              <div className="dashboard-tile status-tile">
                <div className="tile-header">
                  <div className="tile-icon" style={{ backgroundColor: '#E3821E' }}>
                    <FaDatabase size={20} />
                  </div>
                  <div className="tile-title">Database</div>
                </div>
                <div className="tile-content">
                  <div className={`status-indicator ${getStatusClass(systemStatus.database || 'connected')}`}>
                    {getStatusText(systemStatus.database || 'connected')}
                  </div>
                  <p>Database connection status</p>
                </div>
              </div>

              <div className="dashboard-tile status-tile">
                <div className="tile-header">
                  <div className="tile-icon" style={{ backgroundColor: '#E3821E' }}>
                    <FaServer size={20} />
                  </div>
                  <div className="tile-title">Server</div>
                </div>
                <div className="tile-content">
                  <div className={`status-indicator ${getStatusClass(systemStatus.server || 'online')}`}>
                    {getStatusText(systemStatus.server || 'online')}
                  </div>
                  <p>Web server status</p>
                </div>
              </div>

              <div className="dashboard-tile status-tile">
                <div className="tile-header">
                  <div className="tile-icon" style={{ backgroundColor: '#E3821E' }}>
                    <FaBarcode size={20} />
                  </div>
                  <div className="tile-title">Scanner Device</div>
                </div>
                <div className="tile-content">
                  <div className={`status-indicator ${systemStatus.devices?.connected > 0 ? 'status-connected' : 'status-warning'}`}>
                    {systemStatus.devices?.connected || 0} Connected
                  </div>
                  <p>Total: {systemStatus.devices?.total || 0} devices</p>
                </div>
              </div>

              <div className="dashboard-tile status-tile">
                <div className="tile-header">
                  <div className="tile-icon" style={{ backgroundColor: '#E3821E' }}>
                    <FaWifi size={20} />
                  </div>
                  <div className="tile-title">Scans</div>
                </div>
                <div className="tile-content">
                  <div className={`status-indicator ${systemStatus.scans?.total > 0 ? 'status-connected' : 'status-warning'}`}>
                    {systemStatus.scans?.total || 0} Total
                  </div>
                  <p>Today: {systemStatus.scans?.today || 0} scans</p>
                </div>
              </div>

              <div className="dashboard-tile status-tile">
                <div className="tile-header">
                  <div className="tile-icon" style={{ backgroundColor: '#E3821E' }}>
                    <FaChartLine size={20} />
                  </div>
                  <div className="tile-title">Uptime</div>
                </div>
                <div className="tile-content">
                  <div className="status-indicator status-connected">
                    {Math.floor((systemStatus.uptime || 0) / 3600)}h
                  </div>
                  <p>Server uptime</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="status-error">
              <p>Failed to load system status</p>
            </div>
          )}
        </div>
        {/* Background Refresh Loader */}
        {isRefreshing && (
          <div className="fixed-loader" style={{
            position: 'fixed',
            bottom: '24px',
            left: '300px', // Adjust based on sidebar width
            background: 'rgba(255, 255, 255, 0.9)',
            padding: '8px 16px',
            borderRadius: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            zIndex: 1000,
            border: '1px solid #E8EAED',
            fontSize: '13px',
            color: '#5F6368',
            fontWeight: 500
          }}>
            <div className="spinner-small" style={{
              width: '14px',
              height: '14px',
              border: '2px solid #E3821E',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Updating...
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
